/* eslint-disable ember/no-side-effects */
/* RESPONSIBLE TEAM: team-actions */
import type AppService from 'embercom/services/app-service';
import Controller from '@ember/controller';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { debounce } from '@ember/runloop';
import ENV from 'embercom/config/environment';
import { isPresent } from '@ember/utils';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency-decorators';
import { post } from 'embercom/lib/ajax';
import { objectIcons, objectTypes } from 'embercom/models/data/matching-system/matching-constants';
import { capitalize } from '@ember/string';
import type IntlService from 'ember-intl/services/intl';
import type WorkflowConnectorInsertableActionsService from 'embercom/services/workflow-connector-insertable-actions';
import { type TaskGenerator } from 'ember-concurrency';
import {
  AVAILABLE_ACTION_COLUMNS,
  AVAILABLE_ACTION_COLUMNS_FOR_MCP_ACTIONS,
  DEFAULT_ACTION_COLUMN_NAMES,
} from 'embercom/models/workflow-connector/constants/available-action-columns';
import {
  AUDIENCE_FILTER_EVERYONE,
  AUDIENCE_FILTER_CUSTOM,
  AUDIENCE_FILTER_NA,
} from 'embercom/components/workflow-connector/audience-filter-constants';
import CreateActionsDropdownOptionComponent from 'embercom/components/workflow-connector/create-actions-dropdown-option-component';
import type Action from 'embercom/models/workflow-connector/action';
import type McpServer from 'embercom/models/workflow-connector/mcp-server';
import { type Action as HeroBannerAction } from 'embercom/components/common/hero-banner';
import type { Template } from 'embercom/components/workflow-connector/mcp-template-list';
import { isSupportedMcpTemplate } from 'embercom/models/workflow-connector/constants/mcp-templates';

const REQUIRED_PERMISSION = 'can_access_developer_hub';
const DEFAULT_COLLAPSIBLE_LIST_LIMIT = 8;

interface CustomActionsModel {
  mcpServers?: McpServer[];
  templateActions: any[];
  actions: any[];
}

export default class CustomActionsIndex extends Controller {
  declare model: CustomActionsModel;

  queryParams = ['actionId', 'oauth_error'];
  @tracked oauth_error: string | null = null;

  actionIcons = {
    shopify: 'shopify',
    statuspage: 'statuspage',
    stripe: 'stripe',
  } as $TSFixMe;

  @service declare store;
  @service declare appService: AppService;
  @service declare workflowConnectorInsertableActions: WorkflowConnectorInsertableActionsService;
  @service declare intercomEventService: $TSFixMe;
  @service declare router: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare permissionsService: $TSFixMe;

  @tracked usableTemplateActions = this.loadUsableTemplateActions();
  @tracked searchTerm = '';
  @tracked debouncedSearchTerm = '';
  @tracked selectedActionStateTab = 'any';
  @tracked selectedColumnNames;
  @tracked selectedAiContentSegmentIds?: string[];
  @tracked showTemplatePreviewModal = false;
  @tracked selectedActionTemplate: $TSFixMe;
  @tracked showMcpServerModal = false;
  @tracked showMcpActionsModal = false;
  @tracked showEditMcpServerModal = false;
  @tracked showDeleteMcpServerModal = false;
  @tracked selectedMcpServer: McpServer | null = null;
  @tracked defaultDataConnectorsId = 'default_connectors';
  @tracked selectedTemplate: Template | null = null;

  bannerIcon = objectIcons[objectTypes.outboundWebhook];
  constructor() {
    super(...arguments);

    let selectedActionsColumns = localStorage.getItem('selected_actions_columns');
    this.selectedColumnNames =
      selectedActionsColumns !== null
        ? JSON.parse(selectedActionsColumns)
        : DEFAULT_ACTION_COLUMN_NAMES;

    this.intercomEventService.trackAnalyticsEvent({
      action: 'viewed',
      object: 'settings',
      tab_viewed: this.selectedActionStateTab,
    });
  }

  get currentTabLabel() {
    return this.intl.t(
      `new-settings.app-settings.custom-actions.tabs.label-${this.selectedActionStateTab}`,
    );
  }

  get app() {
    return this.appService.app;
  }
  get admin() {
    return this.app.currentAdmin;
  }

  get allWorkflowActions() {
    return (this.model as { actions: Action[] }).actions.filter((action) => !action.mcpServerId);
  }

  get mcpServers(): McpServer[] {
    return (this.model.mcpServers ?? []).filter(
      (server) => server.id && !server.isError && !server.isNew,
    );
  }

  get newHeroBannerActions(): HeroBannerAction[] {
    if (this.app.isStandaloneApp) {
      return [];
    }
    return [
      {
        type: 'link',
        label: 'workflow-connector.new-banner.actions.article-setting-up-actions',
        icon: 'article',
        analyticsId: 'setting-up-actions-article',
        onClick: () => {
          window.Intercom('showArticle', 6298282); // https://www.intercom.com/help/en/articles/6298282-setting-up-a-custom-action
        },
      },
    ];
  }

  loadUsableTemplateActions() {
    if (!this.model) {
      return [];
    }

    return (this.model as { [key: string]: any }).templateActions;
  }

  triggerTour() {
    window.Intercom('startTour', 351602);
  }

  @action
  async onCreateOptionSelected(selectedOption: $TSFixMe) {
    if (!this.validatePermissionAndAllowAction('create-action')) {
      return;
    }

    if (selectedOption === 'create-custom-action') {
      await this.createAction();
    } else {
      this.selectedActionTemplate = this.store.peekRecord(
        'workflow-connector/setup-action',
        selectedOption,
      );
      this.showTemplatePreviewModal = true;
      this.trackAnalyticsEvent(
        'open-template-action-modal',
        'custom-action',
        this.selectedActionTemplate.id,
      );
    }
  }

  @action
  async createAction() {
    let newWorkflowAction = await this.store
      .createRecord('workflow-connector/action', { usage: 'fin' })
      .save();

    await this.store.findRecord('workflow-connector/setup-action', newWorkflowAction.id);

    // Refetch insertable-actions since a new action was created
    this.workflowConnectorInsertableActions.refetch();

    this.trackAnalyticsEvent('created', 'custom-action', newWorkflowAction.id);
    this.router.transitionTo(
      'apps.app.settings.app-settings.custom-actions.custom-action',
      newWorkflowAction.id,
    );
  }

  @task
  *createActionFromTemplate(templateActionId: $TSFixMe): TaskGenerator<void> {
    try {
      let params = {
        app_id: this.app.id,
        template_action_id: templateActionId,
      };
      let createdAction = yield post(
        `/ember/workflow_connector/actions/create_action_from_template`,
        params,
      );
      this.trackAnalyticsEvent('created-from-template', 'custom-action', createdAction.id);
      this.router.transitionTo(
        'apps.app.settings.app-settings.custom-actions.custom-action',
        createdAction.id,
      );
    } catch (exception) {
      this.notificationsService.notifyError(
        this.intl.t('workflow-connector.actions.templates.error'),
      );
    }
  }

  @action
  changeActionStateTab(value: string) {
    this.selectedActionStateTab = value;
  }

  @action
  closeTemplatePreviewModal() {
    this.showTemplatePreviewModal = false;
    this.trackAnalyticsEvent(
      'closed-template-action-modal',
      'custom-action',
      this.selectedActionTemplate.id,
    );
  }

  get workflowActionsToDisplay() {
    return isPresent(this.debouncedSearchTerm)
      ? this.searchedWorkflowActions
      : this.filteredWorkflowActions;
  }

  @action
  mcpActionsToDisplay(mcpServer: McpServer) {
    if (!mcpServer) {
      return [];
    }

    let filteredActions = mcpServer.mcpActions;
    filteredActions = this.filterActionsByState(filteredActions);
    filteredActions = this.filterActionsByAudience(filteredActions);

    if (isPresent(this.debouncedSearchTerm)) {
      let downcasedSearchTerm = this.debouncedSearchTerm.toLocaleLowerCase();
      filteredActions = filteredActions.filter((action) =>
        action.name.toLocaleLowerCase().includes(downcasedSearchTerm),
      );
    }

    return filteredActions;
  }

  private filterActionsByState(actions: any[]): any[] {
    if (this.selectedActionStateTab === 'any') {
      return actions;
    }
    return actions.filter((action) => action.state === this.selectedActionStateTab);
  }

  private filterActionsByAudience(actions: any[]): any[] {
    if (!this.selectedAiContentSegmentIds || this.selectedAiContentSegmentIds.length === 0) {
      return actions;
    }

    return actions.filter((action: any) => {
      let audienceMode = action.audienceMode;

      return this.selectedAiContentSegmentIds!.some((filterId) => {
        if (filterId === AUDIENCE_FILTER_NA) {
          return audienceMode === AUDIENCE_FILTER_NA;
        }
        if (filterId === AUDIENCE_FILTER_EVERYONE) {
          return audienceMode === AUDIENCE_FILTER_EVERYONE;
        }
        if (filterId === AUDIENCE_FILTER_CUSTOM) {
          return audienceMode === AUDIENCE_FILTER_CUSTOM;
        }

        let segmentIds = action.aiContentSegmentIds || [];
        return segmentIds.includes(parseInt(filterId, 10));
      });
    });
  }

  get filteredWorkflowActions() {
    let actions = this.allWorkflowActions;
    actions = this.filterActionsByState(actions);
    actions = this.filterActionsByAudience(actions);
    return actions;
  }

  get searchedWorkflowActions() {
    let downcasedSearchTerm = this.debouncedSearchTerm.toLocaleLowerCase();
    return this.filteredWorkflowActions.filter(({ name }: { name: string }) => {
      return name.toLocaleLowerCase().includes(downcasedSearchTerm);
    });
  }

  get hasItemsToDisplay() {
    return this.filteredWorkflowActions.length > 0 || this.mcpServers.length > 0;
  }

  get unusedTemplates() {
    return this.usableTemplateActions.filter((a: Action) => a.children?.length === 0);
  }

  get createActionsDropdownOptions() {
    if (this.unusedTemplates.length > 0) {
      return [
        this.createCustomActionOption(),
        {
          heading: this.intl.t('workflow-connector.actions.templates.create'),
          items: this.unusedTemplates.map((templateAction: $TSFixMe) => {
            return {
              icon: this.templateActionIcon(templateAction),
              iconUrl: templateAction.iconUrl,
              text: this.templateActionName(templateAction),
              value: `${templateAction.id}`,
              component: CreateActionsDropdownOptionComponent,
            };
          }),
        },
      ];
    } else {
      return [this.createCustomActionOption()];
    }
  }

  get hasMultipleActionOptions() {
    return this.createActionsDropdownOptions.length > 1;
  }

  get columnSelectorData(): { text: string; value: string; isSelected?: boolean }[] {
    return AVAILABLE_ACTION_COLUMNS.filter((column) => !this.isExludedColumn(column.valuePath)).map(
      (column) => {
        return {
          text: this.intl.t(`workflow-connector.list.columns.${column.valuePath}`),
          value: column.valuePath,
          isSelected: this.selectedColumnNames.includes(column.valuePath),
        };
      },
    );
  }

  isExludedColumn(columnValuePath: string) {
    if (columnValuePath === 'name') {
      return true;
    }

    if (this.app.canUseMcpActions && columnValuePath === 'deleteAction') {
      return true;
    }

    if (
      columnValuePath === 'health' &&
      !this.app.canUseFeature('tdf-workflow-connector-health-metrics')
    ) {
      return true;
    }

    return false;
  }

  get selectedColumns() {
    return AVAILABLE_ACTION_COLUMNS.filter(
      (column) =>
        column.valuePath === 'name' ||
        (!this.isExludedColumn(column.valuePath) &&
          this.selectedColumnNames.includes(column.valuePath)),
    );
  }

  get selectedColumnsForMcpActions() {
    let baseColumns = AVAILABLE_ACTION_COLUMNS.filter(
      (column) =>
        column.valuePath === 'name' ||
        (!this.isExludedColumn(column.valuePath) &&
          this.selectedColumnNames.includes(column.valuePath)),
    );

    return [...baseColumns, ...AVAILABLE_ACTION_COLUMNS_FOR_MCP_ACTIONS];
  }

  get defaultCollapsibleListLimit() {
    return DEFAULT_COLLAPSIBLE_LIST_LIMIT;
  }

  @action
  onSelectionChange(selectedColumnNames: string[]) {
    this.selectedColumnNames = selectedColumnNames;
    localStorage.setItem('selected_actions_columns', JSON.stringify(selectedColumnNames));
  }

  templateActionIcon(action: Action) {
    if (action.appPackageCode && this.actionIcons.hasOwnProperty(action.appPackageCode)) {
      return this.actionIcons[action.appPackageCode];
    }

    return action.isRecommendedAction ? 'webhook' : 'new';
  }

  templateActionName(action: Action) {
    if (action.isRecommendedAction) {
      return capitalize(action.name);
    }
    return `${capitalize(action.appPackageCode)}: ${action.name}`;
  }

  createCustomActionOption() {
    return {
      items: [
        {
          icon: 'new',
          text: this.intl.t('workflow-connector.actions.create-custom'),
          value: 'create-custom-action',
        },
      ],
    };
  }

  updateSearchTerm() {
    this.debouncedSearchTerm = this.searchTerm;
  }

  trackAnalyticsEvent(action: string, object: string, actionId = null) {
    this.intercomEventService.trackAnalyticsEvent({
      action,
      object,
      actionId,
    });
  }

  @action
  onChangeSearchTerm() {
    this.trackAnalyticsEvent('searched', 'custom-action');
    debounce(this, this.updateSearchTerm, ENV.APP._250MS);
  }

  validatePermissionAndAllowAction(object: string) {
    if (!this.permissionsService.currentAdminCan(REQUIRED_PERMISSION)) {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(REQUIRED_PERMISSION);
      this.intercomEventService.trackAnalyticsEvent({
        action: 'blocked',
        object,
      });
      return false;
    }
    return true;
  }

  @action
  handleTemplateSelect(template: Template) {
    this.trackTemplateClick(template);

    if (template.id === 'custom-mcp-server' || !template.template) {
      this.trackCustomServerClick();
    }
    this.selectedTemplate = template;
  }

  trackTemplateClick(template: Template) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'mcp_template_card',
      template_id: template.id,
    });
  }

  trackCustomServerClick() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'add_custom_mcp_server',
    });
  }

  @action
  showEditOrDeleteMcpServerModal(server: McpServer) {
    this.selectedMcpServer = server;
    isSupportedMcpTemplate(server.name)
      ? (this.showDeleteMcpServerModal = true)
      : (this.showEditMcpServerModal = true);
  }

  @action
  async deleteMcpServer(server: McpServer) {
    try {
      await server.destroyRecord();

      // Refetch insertable-actions since server deletion affects available actions
      this.workflowConnectorInsertableActions.refetch();
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('workflow-connector.actions.delete-server-error'),
      );
    } finally {
      this.showDeleteMcpServerModal = false;
      this.selectedMcpServer = null;
    }
  }

  @action
  checkForOAuthError() {
    if (this.oauth_error === 'true') {
      this.notificationsService.notifyError(
        this.intl.t('workflow-connector.actions.mcp-actions.add-mcp-server-oauth-error'),
      );

      this.oauth_error = null;
      this.router.transitionTo(this.router.currentRouteName, {
        queryParams: { oauth_error: null },
        replace: true,
      });
    }
  }

  @action
  setAiContentSegmentIds(aiContentSegmentIds?: string[]): void {
    this.selectedAiContentSegmentIds = aiContentSegmentIds;
  }
}
